import * as s from "./schema";
import {extraContentSchema, templateAppearanceSchema, EXTRA_TEXT_FIELDS, TEMPLATE_APPEARANCE_FIELDS, validateGdnTags, type ExtraContent, type TemplateAppearance} from "./template-controls";
import {resolveTemplateCapabilities} from "./template-capabilities";
import {variantsForLayout} from "../data/products/index";
import {validateBannerCombination, resolveBannerSelection} from "../data/template-availability";
import {isTextColorLock, type TextColorLock} from "../libs/textColor";
import {fieldColorsSchema, elementColorsSchema, type FieldColors, type ElementColors} from "../libs/sceneColors";
import {GRID_GENERATIVE_MIN_COUNT, GRID_GENERATIVE_MAX_COUNT, GRID_GENERATIVE_MIN_STOPS, GRID_GENERATIVE_MAX_STOPS, GRID_GENERATIVE_MAX_CONFLICTS} from "./limits";

const contentV1Schema = s.shape({
  title: s.text, subtitle: s.text, subtitle2: s.text, subtitle3: s.text,
  name: s.text, position: s.text, date: s.text, badge: s.text, productName: s.text,
  features: s.text, eap: s.text, link: s.text, link2: s.text, link3: s.text, version: s.text,
  tags: s.dictionary(s.text), cursorLabel: s.text,
});
export const contentSchema: s.Validator<ReturnType<typeof contentV1Schema> & ExtraContent> = (value, path = "content") => {
  const record = s.object(value, path);
  const extraKeys = [...Object.keys(EXTRA_TEXT_FIELDS), "gdnTags", "gdnTagOrder"];
  contentV1Schema(Object.fromEntries(Object.entries(record).filter(([key]) => !extraKeys.includes(key))), path);
  extraContentSchema(Object.fromEntries(Object.entries(record).filter(([key]) => extraKeys.includes(key))), path);
  validateGdnTags(record);
  return value as BannerContent;
};
const flipSchema = s.shape({x: s.boolean, y: s.boolean});
export const generativeCountSchema: s.Validator<number> = (value, path) => {
  const count = s.range(0, GRID_GENERATIVE_MAX_COUNT, true)(value, path);
  if (count !== 0) s.range(GRID_GENERATIVE_MIN_COUNT, GRID_GENERATIVE_MAX_COUNT, true)(count, path);
  return count;
};
export const generativeStopsSchema = s.range(GRID_GENERATIVE_MIN_STOPS, GRID_GENERATIVE_MAX_STOPS, true);
const blobs = {minLength: GRID_GENERATIVE_MIN_COUNT, maxLength: GRID_GENERATIVE_MAX_COUNT};
export const generativeSchema = s.shape({
  paletteKey: s.text, gradients: s.array(s.text, blobs), cssProperties: s.dictionary(s.text),
  keyframes: s.text, animationName: s.text,
  positions: s.array(s.array(s.shape({x: s.finite, y: s.finite}), {minLength: GRID_GENERATIVE_MIN_STOPS, maxLength: GRID_GENERATIVE_MAX_STOPS}), blobs),
  colorMeta: s.shape({assignments: s.array(s.range(0, GRID_GENERATIVE_MAX_COUNT - 1, true), blobs), originalHues: s.array(s.finite, blobs),
    conflictPairs: s.array(s.shape({a: s.range(0, GRID_GENERATIVE_MAX_COUNT - 1, true), b: s.range(0, GRID_GENERATIVE_MAX_COUNT - 1, true), bridgeHue: s.finite}), {maxLength: GRID_GENERATIVE_MAX_CONFLICTS})}),
});
export const baseColorSchema: s.Validator<string> = (value, path) => {
  const string = s.text(value, path);
  if (!/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(string)) s.fail(path, "Expected hex color without #");
  return string;
};
const textColor: s.Validator<TextColorLock | null> = (value, path) => {
  if (value !== null && !isTextColorLock(value)) s.fail(path, "Expected black, light, #rrggbb or null");
  return value as TextColorLock | null;
};
const appearanceV1Schema = s.shape({
  layout: s.range(1, Infinity, true), base: baseColorSchema, backgroundOption: s.text,
  backgroundX: s.finite, backgroundY: s.finite, backgroundScale: s.range(Number.MIN_VALUE),
  backgroundRotate: s.finite, backgroundFlip: flipSchema,
  uploadX: s.finite, uploadY: s.finite, uploadScale: s.range(Number.MIN_VALUE),
  uploadRotate: s.finite, uploadFlip: flipSchema,
  logoX: s.finite, logoY: s.finite, logoScale: s.range(Number.MIN_VALUE),
  font: s.text, icon: s.text, fontVariation: s.finite,
  uppercase: s.boolean, fontToggle: s.boolean, logoHidden: s.boolean, textColor: textColor,
  backgroundBrightness: s.range(0), backgroundBlur: s.range(0),
  backgroundOpacity: s.optional(s.range(0, 100)),
  buttonColor: s.optional(s.nullable((value, path) => {if (value !== "light" && value !== "dark") s.fail(path, "Expected light or dark"); return value as "light" | "dark";})),
  generative: s.nullable(generativeSchema), generativeSpeed: s.range(1),
  generativeCount: generativeCountSchema, generativeStops: generativeStopsSchema, regenerateKey: s.range(0, Infinity, true),
});
export type BannerAppearance = ReturnType<typeof appearanceV1Schema> & {fieldColors?: FieldColors; elementColors?: ElementColors} & TemplateAppearance;
export const appearanceSchema: s.Validator<BannerAppearance> = (value, path = "appearance") => {
  const record = s.object(value, path);
  const {fieldColors, elementColors, ...base} = record;
  const templateKeys = Object.keys(TEMPLATE_APPEARANCE_FIELDS);
  appearanceV1Schema(Object.fromEntries(Object.entries(base).filter(([key]) => !templateKeys.includes(key))), path);
  templateAppearanceSchema(Object.fromEntries(Object.entries(base).filter(([key]) => templateKeys.includes(key))), path);
  if (Object.hasOwn(record, "fieldColors")) fieldColorsSchema(fieldColors, `${path}.fieldColors`);
  if (Object.hasOwn(record, "elementColors")) elementColorsSchema(elementColors, `${path}.elementColors`);
  return value as BannerAppearance;
};
const logoVariant: s.Validator<string | null> = (value, path) =>
  value === null || value === "__NO_LOGO__" ? value as string | null : s.assetUrl(value, path);
export const imageSlotSchema = s.shape({
  url: s.nullable(s.assetUrl), x: s.finite, y: s.finite, scale: s.range(Number.MIN_VALUE), rotate: s.finite, flip: flipSchema,
});
export const assetsSchema = s.shape({
  background: s.nullable(s.assetUrl), image: s.nullable(s.assetUrl),
  logo: s.nullable(s.assetUrl), logoVariant, slots: s.dictionary(imageSlotSchema),
});
export const instanceIdSchema: s.Validator<string> = (value, path = "instanceId") => {
  const id = s.text(value, path);
  if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,79}$/.test(id)) s.fail(path, "Invalid instance ID");
  return id;
};
export const bannerSchema = s.shape({
  instanceId: instanceIdSchema, templateKey: s.text, product: s.text, contentType: s.text, set: s.nullable(s.text),
  content: contentSchema, appearance: appearanceSchema, assets: assetsSchema, exportEnabled: s.boolean,
});
export type BannerContent = ReturnType<typeof contentV1Schema> & ExtraContent;
export type BannerAssets = ReturnType<typeof assetsSchema>;
export type BannerInstance = ReturnType<typeof bannerSchema>;
export type BannerPatch = {content?: Partial<BannerContent>; appearance?: Partial<BannerAppearance>; assets?: Partial<BannerAssets>; exportEnabled?: boolean};
const documentVersion: s.Validator<1 | 2> = (value, path) => {
  if (value !== 1 && value !== 2) s.fail(path, "Unsupported document version");
  return value as 1 | 2;
};
const documentSchema = s.shape({schemaVersion: documentVersion, kind: s.literal("custom-grid"), title: s.text, banners: s.array(bannerSchema)});
export type GridDocument = ReturnType<typeof documentSchema>;

export function bannerUsesV2(banner: BannerInstance) {
  return ["fieldColors", "elementColors", ...Object.keys(TEMPLATE_APPEARANCE_FIELDS)].some((key) => Object.hasOwn(banner.appearance, key)) ||
    [...Object.keys(EXTRA_TEXT_FIELDS), "gdnTags", "gdnTagOrder"].some((key) => Object.hasOwn(banner.content, key)) ||
    Object.keys(banner.assets.slots).some((key) => /^ls-speaker-[1-6]$/.test(key));
}

/** Keep v1 unchanged. Promote only when the document uses v2 state. */
export function promoteGridDocument(document: GridDocument): GridDocument {
  return document.schemaVersion === 1 && document.banners.some(bannerUsesV2)
    ? {...document, schemaVersion: 2} : document;
}

function validateBannerState(banner: BannerInstance) {
  const {product, definition} = validateBannerCombination(banner, banner.appearance.layout);
  const policy = resolveTemplateCapabilities(definition.key, definition.channel, product.code, banner.contentType, banner.appearance.layout);
  if (policy.air && banner.appearance.variant !== undefined && !variantsForLayout(product.code, banner.set ?? undefined, banner.appearance.layout)?.some((entry) => entry.id === banner.appearance.variant)) {
    s.fail("appearance.variant", "Air variant is unavailable on this layout; set an available variant in the same patch");
  }
  if (banner.appearance.backgroundOption === "custom" && banner.assets.background === null) {
    s.fail(`banner.${banner.instanceId}.assets.background`, "Custom background requires an asset");
  }
  if (banner.appearance.backgroundOption === "generative" && banner.appearance.generative === null) {
    s.fail(`banner.${banner.instanceId}.appearance.generative`, "Generative background must be materialized");
  }
  const {generative, generativeCount, generativeStops, regenerateKey} = banner.appearance;
  if (generative !== null) {
    const count = generative.gradients.length;
    if (generative.paletteKey !== `${product.label}:${generativeCount}:${generativeStops}:${regenerateKey}` ||
      (generativeCount !== 0 && count !== generativeCount) || generative.positions.length !== count ||
      generative.positions.some((stops) => stops.length !== generativeStops) ||
      generative.colorMeta.assignments.length !== count || generative.colorMeta.originalHues.length !== count) {
      s.fail(`banner.${banner.instanceId}.appearance.generative`, "Materialized background does not match the banner's generation settings");
    }
  }
}
export function validateGridDocument(value: unknown): asserts value is GridDocument {
  const document = documentSchema(value, "grid");
  const ids = new Set<string>();
  for (const banner of document.banners) {
    if (document.schemaVersion === 1 && bannerUsesV2(banner)) {
      s.fail(`banner.${banner.instanceId}.appearance`, "New template controls require Grid document v2");
    }
    if (ids.has(banner.instanceId)) s.fail("grid.banners", `Duplicate instance ID: ${banner.instanceId}`);
    ids.add(banner.instanceId);
    validateBannerState(banner);
  }
}

export function createGridDocument(title = "Untitled grid"): GridDocument {
  const document: GridDocument = {schemaVersion: 1, kind: "custom-grid", title, banners: []};
  validateGridDocument(document);
  return document;
}
// Creation may supply custom/generative assets in its initial patch. Validate
// the complete materialized banner after merging, just as for an existing edit.
export function patchBanner(value: unknown, patch: BannerPatch): BannerInstance {
  const banner = bannerSchema(value, "banner");
  const fields = s.object(patch, "patch");
  for (const key of Object.keys(fields)) {
    if (!["content", "appearance", "assets", "exportEnabled"].includes(key)) s.fail(`patch.${key}`, "Unknown field");
    if (key !== "exportEnabled") s.object(fields[key], `patch.${key}`);
  }
  patch = structuredClone(patch);
  const next = {
    ...banner, content: {...banner.content, ...patch.content}, appearance: {...banner.appearance, ...patch.appearance},
    assets: {...banner.assets, ...patch.assets},
    exportEnabled: Object.hasOwn(patch, "exportEnabled") ? patch.exportEnabled : banner.exportEnabled,
  };
  if (next.appearance.layout !== banner.appearance.layout && !resolveBannerSelection(next).layouts.includes(next.appearance.layout)) s.fail("appearance.layout", "Unavailable layout for editing");
  bannerSchema(next, "banner");
  validateBannerState(next);
  return next;
}
export function updateBanner(document: GridDocument, instanceId: string, patch: BannerPatch): GridDocument {
  validateGridDocument(document);
  if (!document.banners.some((banner) => banner.instanceId === instanceId)) throw new Error(`Unknown banner: ${instanceId}`);
  const next = {...document, banners: document.banners.map((banner) => banner.instanceId === instanceId ? patchBanner(banner, patch) : banner)};
  return promoteGridDocument(next);
}
